import dayjs from "dayjs"
import { create } from "zustand"

import { DATE_FORMAT } from "@/constants/formats"
import type { PriceChangeItem, PriceChangeItemGroup, PriceChangeType } from "@/graphql/codegen/graphql"
import type { PricingGridRow } from "@/screens/Pricing/components/tables/types"
import { getPriceChangeEditKey } from "@/screens/Pricing/utils/pricingRow"

/**
 * A single persisted pricing edit. Carries both the edited row and the context
 * needed to rebuild a SubmitPriceChangeItemInput at submit time, so edits on
 * pages that are no longer mounted can still be submitted and rehydrated.
 */
export interface PriceChangeEdit {
  row: PricingGridRow
  productId: number
  group: PriceChangeItemGroup
  uomId: string
  priceChangeType: PriceChangeType
  assignedToId?: string
  isManual: boolean
  isModified: boolean
}

interface PriceChangeEditsState {
  edits: Record<string, PriceChangeEdit>
  selectedRowKeys: Record<string, true>
  hasEdits: boolean
  // Kept outside `edits` so a defaulted date never counts as a manual edit.
  defaultedRowKeys: Record<string, true>
}

interface PriceChangeEditsActions {
  upsertEdits: (edits: PriceChangeEdit[]) => void
  removeEdits: (keys: string[]) => void
  setRowsSelected: (keys: string[], selected: boolean) => void
  resetInvalidDates: () => void
  clearEdits: () => void
  addDefaultedRows: (keys: string[]) => void
  removeDefaultedRows: (keys: string[]) => void
  clearDefaultedRows: () => void
}

const INITIAL_STATE: PriceChangeEditsState = {
  edits: {},
  selectedRowKeys: {},
  hasEdits: false,
  defaultedRowKeys: {},
}

export const selectDefaultedRowCount = (state: Pick<PriceChangeEditsState, "defaultedRowKeys">): number =>
  Object.keys(state.defaultedRowKeys).length

export const usePriceTableEditsStore = create<PriceChangeEditsState & PriceChangeEditsActions>((set) => ({
  ...INITIAL_STATE,

  upsertEdits: (edits) =>
    set((state) => {
      if (!edits.length) return state
      const nextEdits = { ...state.edits }
      for (const edit of edits) {
        nextEdits[getPriceChangeEditKey(edit.group.id, edit.row)] = edit
      }
      return { edits: nextEdits, hasEdits: Boolean(Object.keys(nextEdits).length) }
    }),

  removeEdits: (keys) =>
    set((state) => {
      if (!keys.length) return state
      const nextEdits = { ...state.edits }
      const nextSelected = { ...state.selectedRowKeys }
      let changed = false
      for (const key of keys) {
        if (key in nextEdits) {
          delete nextEdits[key]
          delete nextSelected[key]
          changed = true
        }
      }
      return changed
        ? { edits: nextEdits, selectedRowKeys: nextSelected, hasEdits: Boolean(Object.keys(nextEdits).length) }
        : state
    }),

  setRowsSelected: (keys, selected) =>
    set((state) => {
      if (!keys.length) return state
      const nextSelected = { ...state.selectedRowKeys }
      let changed = false
      for (const key of keys) {
        if (selected && !nextSelected[key]) {
          nextSelected[key] = true
          changed = true
        } else if (!selected && nextSelected[key]) {
          delete nextSelected[key]
          changed = true
        }
      }
      return changed ? { selectedRowKeys: nextSelected } : state
    }),

  resetInvalidDates: () =>
    set((state) => {
      const currentDate = dayjs()
      const today = currentDate.format(DATE_FORMAT.ISO_18601)
      const updatedItems = new Map<PriceChangeItem, PriceChangeItem>()
      const nextEdits = { ...state.edits }
      let changed = false

      for (const [key, edit] of Object.entries(state.edits)) {
        const { effectiveDate, expirationDate } = edit.row.item
        if (!effectiveDate || !dayjs(effectiveDate).isBefore(currentDate, "day")) continue

        let updatedItem = updatedItems.get(edit.row.item)
        if (!updatedItem) {
          const hasInvalidExpirationDate = expirationDate && dayjs(expirationDate).isBefore(today, "day")

          updatedItem = {
            ...edit.row.item,
            effectiveDate: today,
            ...(hasInvalidExpirationDate && { expirationDate: today }),
          }
          updatedItems.set(edit.row.item, updatedItem)
        }

        nextEdits[key] = { ...edit, row: { ...edit.row, item: updatedItem } }
        changed = true
      }

      return changed ? { edits: nextEdits } : state
    }),

  clearEdits: () => set({ edits: {}, selectedRowKeys: {}, hasEdits: false, defaultedRowKeys: {} }),

  addDefaultedRows: (keys) =>
    set((state) => {
      const newKeys = keys.filter((key) => !state.defaultedRowKeys[key])
      if (!newKeys.length) return state
      const next = { ...state.defaultedRowKeys }
      for (const key of newKeys) next[key] = true
      return { defaultedRowKeys: next }
    }),

  removeDefaultedRows: (keys) =>
    set((state) => {
      const presentKeys = keys.filter((key) => state.defaultedRowKeys[key])
      if (!presentKeys.length) return state
      const next = { ...state.defaultedRowKeys }
      for (const key of presentKeys) delete next[key]
      return { defaultedRowKeys: next }
    }),

  clearDefaultedRows: () =>
    set((state) => (Object.keys(state.defaultedRowKeys).length ? { defaultedRowKeys: {} } : state)),
}))
